import { useEffect, useRef, useState } from 'react';

import { graphql } from 'react-relay';
import { useRelayEnvironment } from 'react-relay/hooks';
import { fetchQuery, type Subscription } from 'relay-runtime';

import type { useHydratedUserActionQuery } from './__generated__/useHydratedUserActionQuery.graphql';

export type HydratedUser = { accountId: string; name: string | null };

export type HydratedUserResult = {
	user: HydratedUser | null;
	isLoading: boolean;
	error: Error | null;
};

/**
 * Custom React hook to fetch and hydrate user details based on a given Atlassian account ID.
 *
 * This hook manages the loading state, error handling, and cancellation of in-flight requests.
 * It returns an object containing the hydrated user, loading status, and any error encountered,
 * or `null` if no account ID is provided.
 *
 * The hook ensures that only one request is active at a time for a given account ID, and
 * automatically cancels previous requests when the account ID changes or the component unmounts.
 *
 * Suspense avoidance: This hook intentionally does NOT use a suspending Relay API (e.g. `useLazyLoadQuery`).
 * It always returns synchronously and never throws a Promise; instead it exposes an explicit `isLoading` state.
 * This makes it safe to use in parts of the tree that are not wrapped by a React Suspense boundary and prevents
 * unexpected UI stalls. When no `suggestedUserAccountId` is supplied the hook returns `null` (maintaining the
 * existing external contract) rather than suspending, and while a request is in flight it simply reports
 * `isLoading: true` until the network response arrives or errors.
 *
 * @param suggestedUserAccountId - The Atlassian account ID of the user to hydrate, or `null`/`undefined` to reset.
 * @returns An object containing the hydrated user, loading state, and error, or `null` if no account ID is supplied.
 */
export function useHydratedUser(
	suggestedUserAccountId: string | null | undefined,
	shouldHydrate: boolean,
): HydratedUserResult | null {
	const environment = useRelayEnvironment();

	const [user, setUser] = useState<HydratedUser | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const inFlightFor = useRef<string | null>(null);
	const activeSubscription = useRef<Subscription | null>(null);
	const disposed = useRef<boolean>(false);

	useEffect(() => {
		disposed.current = false;
		return () => {
			disposed.current = true;
			activeSubscription.current?.unsubscribe();
			activeSubscription.current = null;
			inFlightFor.current = null;
		};
	}, []);

	useEffect(() => {
		if (!suggestedUserAccountId || !shouldHydrate) {
			// reset state for missing id and do not issue a query
			if (inFlightFor.current) {
				activeSubscription.current?.unsubscribe();
				activeSubscription.current = null;
				inFlightFor.current = null;
			}
			setUser(null);
			setIsLoading(false);
			setError(null);
			return;
		}

		if (user && user.accountId === suggestedUserAccountId && user.name) {
			return; // already hydrated fully
		}
		if (inFlightFor.current === suggestedUserAccountId) {
			return; // request already in flight for this id
		}

		// cancel any previous in-flight subscription for a different id
		if (activeSubscription.current) {
			activeSubscription.current.unsubscribe();
			activeSubscription.current = null;
		}

		inFlightFor.current = suggestedUserAccountId;
		setIsLoading(true);
		setError(null);

		const subscription = fetchQuery<useHydratedUserActionQuery>(
			environment,
			graphql`
				query useHydratedUserActionQuery($accountId: ID!) {
					user(accountId: $accountId) {
						accountId
						name
					}
				}
			`,
			{ accountId: suggestedUserAccountId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response) => {
				if (disposed.current) {
					return;
				}
				if (inFlightFor.current !== suggestedUserAccountId) {
					return;
				} // stale
				if (response?.user?.accountId) {
					setUser({ accountId: response.user.accountId, name: response.user.name });
				} else {
					setUser(null);
				}
				setIsLoading(false);
			},
			error: (error: Error) => {
				if (disposed.current) {
					return;
				}
				if (inFlightFor.current === suggestedUserAccountId) {
					setError(error);
					setIsLoading(false);
				}
			},
			complete: () => {
				if (!disposed.current && inFlightFor.current === suggestedUserAccountId) {
					setIsLoading(false);
				}
			},
		});

		activeSubscription.current = subscription;

		return () => {
			if (activeSubscription.current === subscription) {
				subscription.unsubscribe();
				activeSubscription.current = null;
				if (inFlightFor.current === suggestedUserAccountId) {
					inFlightFor.current = null;
				}
			}
		};
		// We intentionally exclude user/isLoading from deps to avoid refetch loops
	}, [suggestedUserAccountId, environment]); // eslint-disable-line react-hooks/exhaustive-deps

	if (!suggestedUserAccountId) {
		return null; // maintain external contract returning null when no account id supplied
	}

	return { user, isLoading, error };
}
